import { z } from 'zod';

/**
 * What a quadrant can CONTAIN, and the two ratings that describe it.
 *
 * ## Spaces, not things
 *
 * Everything named here is a SPACE: a place where a boss will one day be born,
 * a place a comet occupies, a stretch of water that will one day hold fish.
 * None of it is alive, none of it holds a reward, and none of it rolls
 * anything. A catalogue that knew what a comet pays would be a catalogue the
 * client could read the payout from - so the catalogue knows where, and the
 * entity's future birth knows what.
 *
 * ## Two axes, not one
 *
 * {@link OpenWorldDangerRating} and {@link OpenWorldResourceRating} are
 * independent on purpose. Danger rises towards the centre; wealth is drawn
 * separately, so a rich quadrant can sit on the rim and a central one can be
 * poor. Collapsing them into a single "level" would make every interesting
 * decision the same decision.
 *
 * Both are 1..5 and both are absent until a player has discovered the quadrant,
 * which is why the public shape carries them as nullable.
 */

/**
 * The kinds of space a quadrant is catalogued with.
 *
 * A closed list, and the order is the PLACEMENT order - the largest structures
 * first, so they get the room, and the small ones fitted around them.
 *
 * The order is therefore part of the derivation, and the rule is: a new kind
 * APPENDS, it never inserts. Appending means the new kind is placed last, sees
 * everything already there and moves none of it - which is what keeps a
 * future subsystem from relocating bosses and planets that players already
 * know. Reordering this list would rebuild the whole world.
 */
export const OPEN_WORLD_CONTENT_KINDS = [
  /** A stretch of water. Fishing lives here later; today it is an area. */
  'water_area',
  /** A planet that can be mined. The space only - no ore, no tier, no reward. */
  'mining_planet',
  /** Where a boss will be born. */
  'boss',
  /**
   * A comet.
   *
   * Note what is NOT here: the chance that breaking one yields a jewel. That
   * number belongs to the entity's birth on the server, never to a catalogue
   * a client may one day be shown part of.
   */
  'comet',
  /** Where a mob will be born. */
  'mob',
] as const;

export type OpenWorldContentKind = (typeof OPEN_WORLD_CONTENT_KINDS)[number];

export const openWorldContentKindSchema = z.enum(OPEN_WORLD_CONTENT_KINDS);

/** Ratings run 1..5. Zero is not a rating - it is what a safe port has instead. */
export const OPEN_WORLD_RATING_MIN = 1;
export const OPEN_WORLD_RATING_MAX = 5;

/** How dangerous a quadrant is, once discovered. */
export type OpenWorldDangerRating = 1 | 2 | 3 | 4 | 5;

/** How rich a quadrant is, once discovered. Independent of danger. */
export type OpenWorldResourceRating = 1 | 2 | 3 | 4 | 5;

export const openWorldRatingSchema = z
  .number()
  .int()
  .min(OPEN_WORLD_RATING_MIN)
  .max(OPEN_WORLD_RATING_MAX);

/**
 * How many spaces of each kind a dangerous quadrant carries, inclusive.
 *
 * Permanent per quadrant: drawn once from the world's seed and never re-rolled,
 * so a quadrant a player learned has three bosses has three bosses forever.
 * A safe port has none of any of them - see `ports.ts`.
 */
export const OPEN_WORLD_CONTENT_COUNTS: Readonly<
  Record<OpenWorldContentKind, { readonly min: number; readonly max: number }>
> = Object.freeze({
  water_area: Object.freeze({ min: 1, max: 3 }),
  mining_planet: Object.freeze({ min: 1, max: 3 }),
  boss: Object.freeze({ min: 1, max: 3 }),
  comet: Object.freeze({ min: 3, max: 9 }),
  mob: Object.freeze({ min: 6, max: 12 }),
});

/**
 * The most spaces one quadrant can ever be asked to hold: the sum of the
 * maxima. The geometry check proves a quadrant has room for this many.
 */
export const OPEN_WORLD_MAX_SPACES_PER_QUADRANT = OPEN_WORLD_CONTENT_KINDS.reduce(
  (total, kind) => total + OPEN_WORLD_CONTENT_COUNTS[kind].max,
  0,
);
